// React Dependencies
import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

// Material UI Components
import TextField from "@mui/material/TextField";
import InputAdornment from '@mui/material/InputAdornment';

import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";

import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";

import { TableSortLabel } from "@mui/material";

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CircleIcon from '@mui/icons-material/Circle';
import { Alert, Stack } from "@mui/material";

// Component Imports
import { logout } from "../../../Redux/Actions/userActions";
import EditModal from "../../../Components/EditModal";
import CreateModal from "../../../Components/CreateModal"
import Loading from "../../../Components/Interactive/Loading";
import Paginate from "../../../Utilities/Paginate";
import SuccessAlert from "../../../Components/Interactive/SuccessAlert";
import { formatDate } from "../../../Helpers/formatters";

const OptOutBlacklist = ({ isOpen }) => {
  const [loading, setIsLoading] = useState(false);
  const [optOutBlacklists, setOptOutBlacklists] = useState([]);

  const [successMessage, setSuccessMessage] = useState(undefined);
  const [editModalError, setEditModalError] = useState(undefined);
  const [createModalError, setCreateModalError] = useState(undefined);
  const clearSuccessMessages = () => setSuccessMessage(undefined);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  //#region modal
  
  const [modalIsOpen, setModalOpen] = useState(false);
  const [createModalIsOpen, setCreateModalOpen] = useState(false);

  // Event Handlers - Modals
  const handleOpen = (e) => setModalOpen(true);
  const handleClose = (e) => {
    setModalOpen(false);
    setEditModalError(undefined);
  };
  const handleOpenNew = e => setCreateModalOpen(true);
  const handleCloseNew = (e) => {
    setCreateModalOpen(false);
    setCreateModalError(undefined);
  };

  const [newBlacklistDomainName, setNewBlacklistDomainName] = useState('');
  const [blacklistDomainItemToUpdate, setBlacklistDomainItemToUpdate] = useState(undefined);

  const setItemToEdit = (blacklistItem, index) => {
    setBlacklistDomainItemToUpdate({ id: blacklistItem.id, name: blacklistItem.name, index: index});
    handleOpen();
  };

  //#endregion

  //#region Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(250);
    const [order, setOrder] = useState("desc");
    const [orderBy, setOrderBy] = useState("leadDate");

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    };

    const descendingComparator = (a, b, orderBy) => {
      if (b[orderBy] < a[orderBy]) {
        return -1;
      }
      if (b[orderBy] > a[orderBy]) {
        return 1;
      }
      return 0;
    };

    const getComparator = (order, orderBy) => {
      return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
    };

    const handleRequestSort = (e, id) => {
      const isAsc = orderBy === id && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(id);
    };

    const stableSort = (array, comparator) => {
      return array
        .map((el, index) => [el, index])
        .sort((a, b) => {
          const lead = comparator(a[0], b[0]);
          if (lead !== 0) return lead;
          return a[1] - b[1];
        })
        .map((el) => el[0]);
    };
  //#endregion

  const headCells = [
    {id: "name", label: "Domain"},
    {id: "createdDate", label: "Created Date"},
    {id: "active", label: "Active"},
    {id: "inactiveDate", label: "Inactive Date"},
  ];
  
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const res = await fetch(`/api/optouts/blacklists`, {
        method: "GET",
        credentials: "include",
      });
      if (res.status === 401) {
        dispatch(logout());
        return navigate("/");
      }
      const result = await res.json();
      setOptOutBlacklists(result.result ?? []);
      setIsLoading(false);
    })();
  }, [dispatch, navigate]);

  const userAuth = useSelector((state) => state.auth);
  const isAdmin = userAuth?.userInfo?.userData?.role === "Administrator" || userAuth?.userInfo?.userData?.role === "Super User";

  
  // Dont show the page if the user is not an admin
  if(!isAdmin)
  {
    return (<Navigate to={{pathname: "/",}}/>);
  }

  const submitBlacklistNew = async () => {

    const options = {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: newBlacklistDomainName,
      }),
    };

    const res = await fetch(`/api/optouts/blacklist/create`, options);
    const parsedResult = await res.json();

    if(parsedResult.success) {
      setOptOutBlacklists([parsedResult.result].concat(optOutBlacklists));
      setSuccessMessage("Domain successfully added!");
      setTimeout(() => clearSuccessMessages(), 5000);
      setCreateModalOpen(false);
    }
    else{
      setCreateModalError(parsedResult.error);
    }
  }

  // Update an existing blacklisted domain
  const submitBlacklistUpdate = async () => {
    const options = {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        id: blacklistDomainItemToUpdate.id,
        name: blacklistDomainItemToUpdate.name,
      }),
    };

    const res = await fetch(`/api/optouts/blacklist/update`, options);
    const parsedResponse = await res.json();

    if(parsedResponse.success) {
      setSuccessMessage("Domain successfully updated!");
      optOutBlacklists[blacklistDomainItemToUpdate.index] = parsedResponse.result;
      setTimeout(() => clearSuccessMessages(), 5000);
      setModalOpen(false);
    }
    else {
      setEditModalError(parsedResponse.error);
    }
  };

  // Delete an existing blacklisted domain
  const submitBlacklistDelete = async () => {
    const options = {
      method: "DELETE",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        id: blacklistDomainItemToUpdate.id,
      }),
    };

    const res = await fetch(`/api/optouts/blacklist/delete`, options);
    const parsedResponse = await res.json();

    if(parsedResponse.success) {
      setSuccessMessage("Domain successfully deleted!");
      optOutBlacklists[blacklistDomainItemToUpdate.index] = parsedResponse.result;
      setTimeout(() => clearSuccessMessages(), 5000);
      setModalOpen(false);
    }
    else {
      setEditModalError(parsedResponse.error);
    }
  };

  return (
    <div className={`${isOpen ? "page" : "page-closed"} `}>
      <Stack direction="row" spacing={2}>
        <h3 className="heading">Opt Out Blacklist Domains</h3>

        <div style={{display: "flex", alignItems: "center", justifyContent:   "space-evenly", float: "right", cursor: "pointer"}}>
          <AddCircleOutlineIcon classs="edit-info-btn" onClick={handleOpenNew}/>
        </div>
      </Stack>
      
      {loading && <Loading />}

      <div className="root">
        {
          successMessage ?
          (<div style={{ position: "fixed", top: "20px", right: "20px" }}>
            <SuccessAlert className="alert-animation" >
              {successMessage}
            </SuccessAlert>
          </div>
          )
          : null
        }
        
        <TableContainer>
          <Table className="table" size={"small"}>
            <TableHead>
              <TableRow className="header">
                {headCells.map(({id, label}) => (
                  <TableCell
                    key={id}
                    className="headerText"
                    align="center"
                    padding="normal"
                    size="medium"
                  >
                    <TableSortLabel
                      active={orderBy === id ? true : false}
                      direction={orderBy === id ? order : "asc"}
                      onClick={(e) => handleRequestSort(e, id)}
                    >
                      {label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell
                    key="action"
                    className="headerText"
                    align="center"
                    padding="normal"
                    size="medium"
                  >
                  </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {optOutBlacklists && optOutBlacklists.length > 0 &&
               stableSort(optOutBlacklists, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((item, index) => {
                  return (
                    <TableRow key={index} >
                      <TableCell className="tableCell" align="center">
                        {item.active ? item.name : <strike>{item.name}</strike>}
                      </TableCell>
                      <TableCell className="tableCell" align="center">
                        {formatDate(item.createdDate)}
                      </TableCell>
                      <TableCell className="tableCell" align="center">
                        {
                          item.active ? 
                          <CircleIcon sx={{color: "green !important"}} />
                          : <CircleIcon sx={{color: "red !important"}} />
                        }
                      </TableCell>
                      <TableCell className="tableCell" align="center">
                        {item.inactiveDate ? formatDate(item.inactiveDate) : "-"}
                      </TableCell>
                      <TableCell className="tableCell" align="center">
                      {
                        item.active &&
                        <EditIcon className="edit-info-btn" onClick={() => setItemToEdit(item, index)} style={{marginRight: "1rem"}}/>
                      }
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <Paginate 
          totalRowCount={optOutBlacklists.length}
          page={page} 
          rowsPerPage={rowsPerPage} 
          rowsPerPageOptions={[50, 100, 250, 500]}
          handleChange={handleChangePage} 
          handleChangeRowsPerPage={handleChangeRowsPerPage} />
      </div>

      <EditModal open={modalIsOpen} handleClose={handleClose} customCSS={{height: "auto"}}>
        <div className="modal-header">
          <h2 style={{ padding: ".5rem", color: "#6acde6" }}>
            Edit Blacklist Domain Name
          </h2>
          <CloseIcon
            className="close-icons"
            onClick={handleClose}
          />
        </div>
        <br/>
        <Alert severity="info">NOTE: You may only be able to update domain name if it's Active and NO opt-out request has come through from the current domain</Alert>
        <br/>
        <TextField
          id="outlined-full-width"
          className="input-el"
          label="Domain"
          name="blacklist_domain_name_update"
          style={{ margin: 10, width: "96%" }}
          fullWidth={false}
          margin="normal"
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          value={blacklistDomainItemToUpdate && blacklistDomainItemToUpdate.name}
          onChange={(e) => setBlacklistDomainItemToUpdate({ id: blacklistDomainItemToUpdate.id,   name: e.target.value, index: blacklistDomainItemToUpdate.index})}
          InputProps={{
            startAdornment: <InputAdornment position="start"><AlternateEmailIcon /></InputAdornment>,
          }}
        />
        {editModalError &&
          <Alert severity="error">{editModalError}</Alert>
        }
        <br/>
        <div className="modal-btn-box">
          <button className="submit-btn" style={{marginTop: "auto"}} onClick={submitBlacklistUpdate}>
            <span>Update</span>
          </button>
          
          <button className="submit-btn" style={{marginTop: "auto"}} onClick={submitBlacklistDelete}>
            <span>Delete</span>
          </button>
        </div>
      </EditModal>
      
      <CreateModal open={createModalIsOpen} handleClose={handleCloseNew} customCSS={{height: "auto"}} >
        <div className="modal-header">
          <h2 style={{ padding: ".5rem", color: "#6acde6" }}>
            Add Domain to the Blacklist
          </h2>
          <CloseIcon
            className="close-icons"
            onClick={handleCloseNew}
          />
        </div>
        <br/>
        <Alert severity="info"><b>NOTE: You may only be able to add a new domain name if it does not exist or is not active</b></Alert>
        <br/>
        <TextField
          id="outlined-full-width"
          className="input-el"
          label="Domain"
          name="blacklist_domain_name_new"
          style={{ margin: 10, width: "96%" }}
          fullWidth={false}
          margin="normal"
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          value={newBlacklistDomainName}
          onChange={(e) => setNewBlacklistDomainName(e.target.value)}
          color="secondary"
          InputProps={{
            startAdornment: <InputAdornment position="start"><AlternateEmailIcon /></InputAdornment>,
          }}
        />
        {createModalError &&
          <Alert severity="error">{createModalError}</Alert>
        }
        <div className="modal-btn-box">
          <button className="submit-btn" onClick={submitBlacklistNew} style={{marginTop: "auto"}}>
            <span>Submit</span>
          </button>
        </div>
      </CreateModal>
    </div>
  );
};

export default OptOutBlacklist;
